import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shiftApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useShiftStore } from '@/store/shiftStore';
import { useSyncStore } from '@/store/syncStore';
import { isCompanyAdmin, isManager } from '@/types';
import { getCachedSettings, isLocalFirstMode } from '@/offline';
import ShiftExpiredModal from '@/components/pos/ShiftExpiredModal';
import ShiftPrintModal from '@/components/pos/ShiftPrintModal';
import ShiftRequiredPanel from '@/components/pos/ShiftRequiredPanel';
import ShiftStatusBar from '@/components/pos/ShiftStatusBar';
import { Loader2 } from 'lucide-react';

type Props = { children: React.ReactNode };

export default function ShiftGate({ children }: Props) {
  const { role } = useAuthStore();
  const {
    openShift,
    setOpenShift,
    shiftJustClosed,
    pendingPrintShiftName,
    setPendingPrintShiftName,
  } = useShiftStore();
  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const [bypassShift, setBypassShift] = useState(false);
  const [forceShowClose, setForceShowClose] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const admin = isCompanyAdmin(role);
  const manager = isManager(role);
  const postCloseUi = Boolean(pendingPrintShiftName || shiftJustClosed);

  useEffect(() => {
    void (async () => {
      if (!isLocalFirstMode(posSyncMode)) {
        setBypassShift(false);
        return;
      }
      const settings = await getCachedSettings<{ offline_bypass_shift_in_local_modes?: boolean }>();
      setBypassShift(settings?.offline_bypass_shift_in_local_modes !== false);
    })();
  }, [posSyncMode]);

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: ['openShift'],
    queryFn: async () => {
      const res = await shiftApi.getOpenShift();
      const shift = res.data.message;
      setOpenShift(shift || null);
      return shift;
    },
    enabled: !bypassShift,
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });



  const printModal = pendingPrintShiftName ? (
    <ShiftPrintModal
      shiftName={pendingPrintShiftName}
      title="Z Report"
      onClose={() => setPendingPrintShiftName(null)}
    />
  ) : null;

  if (bypassShift) {
    return <div className="flex-1 flex flex-col overflow-hidden min-w-0">{children}</div>;
  }

  if ((isLoading || (isFetching && !openShift)) && !postCloseUi) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 min-w-0">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!openShift) {
    if (manager || admin) {
      return (
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <ShiftStatusBar shift={null} onStartShift={() => { void refetch(); }} />
          {children}
          {printModal}
        </div>
      );
    }
    return (
      <>
        <ShiftRequiredPanel onShiftStarted={() => { void refetch(); }} />
        {printModal}
      </>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-w-0">
      <ShiftStatusBar
        shift={openShift}
        onStartShift={() => { void refetch(); }}
        forceShowClose={forceShowClose}
        onForceCloseHandled={() => setForceShowClose(false)}
        onCloseModalChange={setCloseModalOpen}
      />
      {openShift.is_expired && !closeModalOpen && (
        <ShiftExpiredModal
          shift={openShift}
          onCloseShift={() => {
            setCloseModalOpen(true);
            setForceShowClose(true);
          }}
        />
      )}
      {children}
      {printModal}
    </div>
  );
}
